import React, { useState } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import { motion } from 'framer-motion';
import DynamicButton from '../components/dynamic/DynamicButton';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaGithub, FaInstagram, FaYoutube, FaTwitter } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { contactAPI } from '../services/api';
import { ContactMessage, SocialLink } from '../types';

interface ContactInfo {
  icon: IconType;
  label: string;
  value: string;
  link: string | null;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactMessage>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      await contactAPI.submit(formData);
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your message! I will get back to you soon.',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo: ContactInfo[] = [
    {
      icon: FaEnvelope,
      label: 'Email',
      value: 'chaunal.adarsh@gmail.com',
      link: 'mailto:chaunal.adarsh@gmail.com',
    },
    {
      icon: FaPhone,
      label: 'Phone',
      value: '+91 7351579058',
      link: 'tel:+917351579058',
    },
    {
      icon: FaMapMarkerAlt,
      label: 'Location',
      value: '3076, Sector 71, Mohali, PB 160071',
      link: null,
    },
  ];

  const socialLinks: SocialLink[] = [
    { icon: FaLinkedin, url: 'https://www.linkedin.com/in/adarsh-chaunal-016974157/', label: 'LinkedIn' },
    { icon: FaGithub, url: 'https://github.com/adarsh-chaunal', label: 'GitHub' },
    { icon: FaInstagram, url: 'https://www.instagram.com/adarsh_chaunal/', label: 'Instagram' },
    { icon: FaYoutube, url: 'https://www.youtube.com/@adarshchaunal', label: 'YouTube' },
    { icon: FaTwitter, url: 'https://x.com/adarshchaunal', label: 'X (Twitter)' },
  ];

  return (
    <div className="pt-20">
      <DynamicSection
        title="Get In Touch"
        subtitle="I'd love to hear from you. Send me a message and I'll respond as soon as possible."
        className="bg-white dark:bg-gray-900"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Contact Information</h3>
              <div className="space-y-6 mb-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start">
                    <div className="text-primary-600 dark:text-primary-400 mr-4 mt-1">
                      <info.icon size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">{info.label}</div>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <div className="text-gray-600 dark:text-gray-400">{info.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Connect on Social Media</h4>
                <div className="flex flex-wrap gap-4">
                  {socialLinks.map(({ icon: Icon, url, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-2"
                      aria-label={label}
                      title={label}
                    >
                      <Icon size={28} />
                      <span className="text-sm">{label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitStatus.type && (
                  <div
                    className={`p-4 rounded-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800'
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <DynamicButton
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </DynamicButton>
              </form>
            </motion.div>
          </div>
        </div>
      </DynamicSection>
    </div>
  );
};

export default Contact;
